import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { PasswordField } from '../components/ui/PasswordField'
import { Button } from '../components/ui/Button'

const Schema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Parola trebuie să aibă cel puțin 8 caractere')
      .max(128)
      .refine((v) => /[A-Z]/.test(v), 'Cel puțin o literă mare')
      .refine((v) => /[a-z]/.test(v), 'Cel puțin o literă mică')
      .refine((v) => /\d/.test(v), 'Cel puțin o cifră'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Parolele nu coincid',
  })

type Input = z.infer<typeof Schema>

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')

  const [submitting, setSubmitting] = useState(false)

  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  if (!token) {
    return (
      <div className="flex h-screen items-center justify-center bg-b4l-bg">
        <div className="w-full max-w-sm rounded-lg border border-b4l-border bg-white p-8 shadow-sm">
          <h1 className="mb-2 text-xl font-semibold text-b4l-red">Link invalid</h1>
          <p className="mb-4 text-sm text-b4l-text-muted">
            Linkul de resetare pare incomplet. Cere unul nou pe pagina de &quot;Ai uitat
            parola?&quot;.
          </p>
          <Link
            to="/forgot-password"
            className="block text-center text-sm text-b4l-green-dark hover:underline"
          >
            Cere link nou
          </Link>
        </div>
      </div>
    )
  }

  const onSubmit = async (data: Input) => {
    setSubmitting(true)
    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: data.newPassword,
      })
      toast.success('Parola a fost schimbată. Te poți autentifica acum.')
      navigate('/login', { replace: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la resetare'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-b4l-bg">
      <div className="w-full max-w-sm rounded-lg border border-b4l-border bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-b4l-green-dark">Setare parolă nouă</h1>
        <p className="mb-6 text-sm text-b4l-text-muted">
          Alege o parolă nouă. Linkul este valabil o oră.
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <PasswordField
            label="Parola nouă"
            autoComplete="new-password"
            error={form.formState.errors.newPassword?.message}
            {...form.register('newPassword')}
          />
          <PasswordField
            label="Confirmă parola nouă"
            autoComplete="new-password"
            error={form.formState.errors.confirmPassword?.message}
            {...form.register('confirmPassword')}
          />
          <Button type="submit" loading={submitting} className="w-full">
            Salvează parola nouă
          </Button>
        </form>
      </div>
    </div>
  )
}
